import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { FC, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import DayPicker from "~/components/DayPicker";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Textarea } from "~/components/ui/textarea";
import {
  ChronologyFormValues,
  EMPTY_CHRONOLOGY_FORM_VALUES,
} from "../helpers/chronologyRequest";
import { createChronologyEventSchema } from "../validations/chronologyEvent";

interface ChronologyEventEditorProps {
  initialValues?: ChronologyFormValues;
  isSaving: boolean;
  isLoadingSuggestion?: boolean;
  submitLabelId: string;
  onSubmit: (values: ChronologyFormValues) => void;
  onCancel: () => void;
}

export const ChronologyEventEditor: FC<ChronologyEventEditorProps> = ({
  initialValues,
  isSaving,
  isLoadingSuggestion,
  submitLabelId,
  onSubmit,
  onCancel,
}) => {
  const intl = useIntl();

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ChronologyFormValues>({
    resolver: zodResolver(createChronologyEventSchema(intl)),
    defaultValues: initialValues ?? EMPTY_CHRONOLOGY_FORM_VALUES,
  });

  useEffect(() => {
    if (!initialValues) return;
    reset(initialValues, { keepDirtyValues: true });
  }, [initialValues, reset]);

  const handleFormSubmit = handleSubmit((values) => {
    onSubmit(values);
  });

  return (
    <form onSubmit={handleFormSubmit} className="flex flex-col gap-3">
      {isLoadingSuggestion && (
        <div className="flex items-center gap-2 rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" />
          <FormattedMessage
            id="chronology.ai.loading"
            defaultMessage="Pre-filling fields with AI…"
          />
        </div>
      )}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="chronology-event-type">
          <FormattedMessage
            id="chronology.field.eventType"
            defaultMessage="Event Type"
          />
        </Label>
        <Input id="chronology-event-type" {...register("eventType")} />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="chronology-event-detail">
          <FormattedMessage
            id="chronology.field.detail"
            defaultMessage="Detail"
          />
        </Label>
        <Textarea
          id="chronology-event-detail"
          rows={3}
          placeholder={intl.formatMessage({
            id: "chronology.field.detailPlaceholder",
          })}
          aria-invalid={Boolean(errors.detail)}
          {...register("detail")}
        />
        {errors.detail && (
          <p className="text-xs text-destructive">{errors.detail.message}</p>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="chronology-event-date">
          <FormattedMessage id="chronology.field.date" defaultMessage="Date" />
        </Label>
        <Controller
          control={control}
          name="eventDate"
          render={({ field }) => (
            <DayPicker
              value={field.value}
              onChange={field.onChange}
              maxDate={new Date()}
            />
          )}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="chronology-event-provider">
          <FormattedMessage
            id="chronology.field.provider"
            defaultMessage="Provider"
          />
        </Label>
        <Input id="chronology-event-provider" {...register("provider")} />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="chronology-event-facility">
          <FormattedMessage
            id="chronology.field.facility"
            defaultMessage="Facility"
          />
        </Label>
        <Input id="chronology-event-facility" {...register("facility")} />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="chronology-event-specialty">
          <FormattedMessage
            id="chronology.field.providerSpecialty"
            defaultMessage="Provider Specialty"
          />
        </Label>
        <Input
          id="chronology-event-specialty"
          {...register("providerSpecialty")}
        />
      </div>

      <div className="flex justify-end gap-2 pt-1">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onCancel}
          disabled={isSaving}
        >
          <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
        </Button>
        <Button type="submit" size="sm" disabled={isSaving}>
          {isSaving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
          <FormattedMessage id={submitLabelId} />
        </Button>
      </div>
    </form>
  );
};
